/**
 * A step's rationale and the actions the model wrote inside it.
 *
 * Harnesses ask the model to think in prose and then emit its actions as a fenced
 * code block in the same message. The runner records that whole message as the
 * step's `rationale`, so the page was printing the code twice: once as "why", once
 * as the executed action. The fenced blocks are the actions, not the reasoning —
 * they belong under Action and nowhere else.
 */
export interface SplitRationale {
  /** The prose, with every fenced block lifted out. Empty if it was all code. */
  rationale: string
  /** The fenced blocks, in the order the model wrote them. */
  blocks: { language: string; code: string }[]
}

/** A closed fence, or — for a message the provider cut off — one left open at the end. */
const FENCE = /```[ \t]*([\w+.-]*)[ \t]*\r?\n?([\s\S]*?)(?:```|$)/g

export function splitRationale(text?: string): SplitRationale {
  if (!text) return { rationale: '', blocks: [] }
  const blocks: SplitRationale['blocks'] = []
  const prose = text.replace(FENCE, (_match, language: string, code: string) => {
    const body = code.replace(/\s+$/, '')
    if (body.trim()) blocks.push({ language: language || '', code: body })
    return '\n'
  })
  // Fences usually sit on their own lines, so lifting one leaves a hole: close it
  // rather than printing a rationale that starts three blank lines down.
  return { rationale: prose.replace(/\n{3,}/g, '\n\n').trim(), blocks }
}
